/**
 * handoffCenterModel — pure projection behind the Handoff Center page
 * (DIGEMP-HANDOFF-CENTER-FRONTEND-084).
 *
 * Nothing here is a new backend concept. Every field comes from the two existing read-only
 * conversation surfaces:
 *   - conversationsApi.list()         → Conversation + ConversationParticipant rows (including
 *                                       handoff_reason and the enriched employee identity)
 *   - conversationsApi.listMessages() → ConversationMessage rows
 *
 * A handoff event IS a `handoff_notice` ConversationMessage — the only handoff record the product
 * actually writes today. conversation_service writes it from exactly two places:
 *   - handoff_conversation          → technical_trace.handoff.from === "operator"
 *   - _handle_ask_manager_and_route → technical_trace.handoff.from === "manager"
 * Conversation.status values "handed_off"/"awaiting_human" exist in CONVERSATION_STATUSES but no
 * code path ever writes them, so this model deliberately never reads Conversation.status.
 *
 * Truthfulness rules this module enforces, because the page must not overstate what is known:
 *   - a notice whose trace carries no `handoff` block yields an event with source "unknown" and a
 *     null target, never a guessed employee
 *   - findings/evidence are attributed to the handoff's *target participant* only, and are null
 *     when that employee produced nothing after the handoff
 *   - "waiting for the operator" is taken from the employee's own reported run stage, never from a
 *     text heuristic over its reply
 *   - a card's classification comes from the same predicate the page's incoming/outgoing partition
 *     uses, so one card can never wear both directions, and the outgoing arm names a destination
 *     only when the notice named an employee
 */
import { TRACE_EVIDENCE_SOURCE_NONE } from "./evidenceSourceVocabulary";
import { handoffSourceLabel } from "./handoffSourceVocabulary";

const AWAITING_RUN_STAGES = new Set(["awaiting_technician_review", "awaiting_approval"]);

export const HANDOFF_DIRECTION_LABELS = {
  incoming: "נכנסת · ממתין לך",
  outgoing: "יוצאת · לעובד דיגיטלי",
};

export const AWAITING_STAGE_LABELS = {
  awaiting_technician_review: "ממתין לסקירתך",
  awaiting_approval: "ממתין לאישורך",
};

/** Event is "incoming" (waiting on a human) exactly when the employee reported one of these. */
export function isAwaitingOperator(event) {
  return (event?.awaitingStages || []).length > 0;
}

/** The employee a card leads with: the resolved participant, else the name the notice itself gave. */
export function handoffEmployeeName(event) {
  return event?.target?.name || event?.targetNameHint || null;
}

/**
 * The one classification a card may wear, or null when the model established nothing to state.
 *
 * The page's two tabs partition events on `isAwaitingOperator`, so a card reads that same
 * predicate: a handoff is *incoming* while the employee reports it is waiting on a human, and
 * *outgoing* otherwise. Only the outgoing arm names a destination ("לעובד דיגיטלי"), so it is
 * drawn only for a handoff the model could attribute to an employee — the same refusal this module
 * already applies to findings and evidence. The incoming arm states the operator's own state and
 * needs no attribution.
 */
export function handoffDirection(event) {
  if (isAwaitingOperator(event)) return "incoming";
  return handoffEmployeeName(event) ? "outgoing" : null;
}

function sortedMessages(messages) {
  return [...(messages || [])].sort((a, b) => (a?.sequence_no ?? 0) - (b?.sequence_no ?? 0));
}

function lastMatching(items, predicate) {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    if (predicate(items[i])) return items[i];
  }
  return null;
}

function targetIdentity(participant) {
  if (!participant) return null;
  return {
    participantId: participant.id,
    agentId: participant.agent_id || null,
    name: participant.agent_name || null,
    roleTitle: participant.agent_role_title || null,
    avatarKey: participant.agent_avatar_key || null,
    avatarColor: participant.agent_avatar_color || null,
    availabilityStatus: participant.agent_availability_status || null,
    isActive: participant.is_active !== false,
  };
}

function evidenceFrom(traceMessage) {
  const trace = traceMessage.technical_trace || {};
  const runId = traceMessage.agent_run_id || trace.agent_run_id || null;
  const actionId = traceMessage.agent_action_id || trace.agent_action_id || null;
  // The producer's "no evidence" sentinel, shared with ConversationPanel's trace box rather than
  // spelled out here: this is the third site that decides the row by comparing against the string,
  // so the vocabulary's pinned sentinel has to be the one all three read.
  const source = trace.evidence_source && trace.evidence_source !== TRACE_EVIDENCE_SOURCE_NONE
    ? trace.evidence_source : null;
  const summary = (trace.evidence_summary || "").trim() || null;
  const tool = trace.tool || null;
  // A trace row with no tool, no source and no summary carries no observable evidence — saying so
  // is more useful than rendering an empty drawer.
  if (!tool && !source && !summary && !runId && !actionId) return null;
  return {
    tool,
    actionStatus: trace.action_status || null,
    source,
    summary,
    error: trace.error || null,
    runId,
    actionId,
    at: traceMessage.created_at || null,
  };
}

function unresolvedState(conversationMessages) {
  // Newest trace per run wins — the same rule ConversationPanel uses, so a run that has since
  // completed stops counting as awaiting.
  const stageByRun = new Map();
  for (const message of conversationMessages) {
    if (message?.message_type !== "technical_trace" || !message.technical_trace) continue;
    const runId = message.agent_run_id || message.technical_trace.agent_run_id;
    stageByRun.set(runId || `trace-${message.sequence_no}`, message.technical_trace);
  }
  const awaitingStages = [];
  for (const trace of stageByRun.values()) {
    const stage = trace.run_stage || null;
    if (stage && AWAITING_RUN_STAGES.has(stage)) {
      awaitingStages.push({ stage, label: trace.run_stage_label || AWAITING_STAGE_LABELS[stage] || stage });
    }
  }

  const lastTurn = lastMatching(
    conversationMessages,
    (m) => m.message_type === "operator_message" || m.message_type === "human_response",
  );
  return {
    awaitingStages,
    employeeSpokeLast: lastTurn?.message_type === "human_response",
    lastTurnAt: lastTurn?.created_at || null,
    lastTurnText: (lastTurn?.content || "").trim() || null,
  };
}

/** Handoff events for one conversation, oldest first. */
export function handoffEventsForConversation(conversation, messages) {
  const conversationId = conversation?.id || null;
  if (!conversationId) return [];
  const participants = conversation.participants || [];
  const agentsByAgentId = new Map(
    participants.filter((p) => p.role === "agent" && p.agent_id).map((p) => [String(p.agent_id), p]),
  );
  const ordered = sortedMessages(messages);

  const events = [];
  for (const notice of ordered) {
    if (notice?.message_type !== "handoff_notice") continue;
    const trace = notice.technical_trace || {};
    const handoff = trace.handoff || null;
    const toAgentId = handoff?.to ? String(handoff.to) : null;
    const targetParticipant = toAgentId ? agentsByAgentId.get(toAgentId) || null : null;
    const after = ordered.filter((m) => (m?.sequence_no ?? 0) > (notice.sequence_no ?? 0));
    const afterByTarget = targetParticipant
      ? after.filter((m) => m.author_participant_id === targetParticipant.id)
      : [];

    const findingMessage = lastMatching(
      afterByTarget,
      (m) => m.message_type === "human_response" && (m.content || "").trim(),
    );
    const evidenceMessage = lastMatching(
      afterByTarget,
      (m) => m.message_type === "technical_trace" && m.technical_trace,
    );
    const unresolved = unresolvedState(after);

    events.push({
      id: notice.id,
      conversationId,
      ticketId: conversation.ticket_id || null,
      customerId: conversation.customer_id || null,
      deviceIdentifier: conversation.device_identifier || null,
      at: notice.created_at || null,
      source: handoff?.from === "operator" || handoff?.from === "manager" ? handoff.from : "unknown",
      sourceLabel: handoffSourceLabel(handoff?.from),
      noticeText: (notice.content || "").trim() || null,
      reason: (handoff?.reason || "").trim() || targetParticipant?.handoff_reason || null,
      routing: handoff
        ? {
            toName: handoff.to_name || null,
            score: typeof handoff.score === "number" ? handoff.score : null,
            matchedSpecialties: handoff.matched_specialties || [],
            matchedTerms: handoff.matched_terms || [],
            availability: handoff.availability || null,
          }
        : null,
      target: targetIdentity(targetParticipant),
      targetNameHint: handoff?.to_name || null,
      findings: findingMessage
        ? { text: findingMessage.content.trim(), at: findingMessage.created_at || null }
        : null,
      evidence: evidenceMessage ? evidenceFrom(evidenceMessage) : null,
      ...unresolved,
    });
  }
  return events;
}

/**
 * Flatten per-conversation loads into one newest-first event list.
 * `loaded` is `[{ conversation, messages }]`; a load that failed contributes nothing and is
 * reported back in `failedConversationIds` so the page can say so instead of showing a gap.
 */
export function buildHandoffCenter(loaded) {
  const events = [];
  const failedConversationIds = [];
  for (const entry of loaded || []) {
    if (!entry) continue;
    if (entry.failed) {
      if (entry.conversationId) failedConversationIds.push(entry.conversationId);
      continue;
    }
    events.push(...handoffEventsForConversation(entry.conversation, entry.messages));
  }
  events.sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")));
  const awaiting = events.filter(isAwaitingOperator);
  return {
    events,
    outgoing: events.filter((e) => !isAwaitingOperator(e)),
    incoming: awaiting,
    withoutEvidence: events.filter((e) => !e.evidence),
    withoutFindings: events.filter((e) => !e.findings),
    failedConversationIds,
  };
}

/** A conversation is only worth loading if a handoff could have been written in it. */
export function isHandoffCandidate(conversation) {
  const participants = conversation?.participants || [];
  return participants.some((p) => p.role === "manager" || (p.role === "agent" && p.handoff_reason));
}
